import {
  AuthResultSchema,
  SessionSchema,
} from '@contracts/schemas/user.schema';
import type { AuthGateway, Credentials, Session } from '@/application/ports/AuthGateway';
import { ApiError, request } from '../client';
import { forgetCachedWork } from '../../pwa/workCache';
import { clearAccessToken, setAccessToken } from '../tokenStore';

/**
 * The one thing remember me keeps on the device: the username, so the box is
 * filled in next time. Never a token. The access token stays in memory and the
 * refresh token stays an HttpOnly cookie, section 7.2 and item 12 of section 17.
 */
const REMEMBERED_USERNAME_KEY = 'ceramica.remembered-username';

/**
 * Guarded, as the install preferences are: private browsing and some managed
 * devices make localStorage throw, and not remembering a username is never a
 * reason to fail a sign in.
 */
function rememberUsername(username: string | null): void {
  try {
    if (username === null) localStorage.removeItem(REMEMBERED_USERNAME_KEY);
    else localStorage.setItem(REMEMBERED_USERNAME_KEY, username);
  } catch {
    /* see above */
  }
}

/**
 * The HTTP implementation of the auth port. Swapping the mock server for the
 * real API changes nothing here, because MSW intercepts at the network layer
 * and this code runs unchanged either way. Section 8.4.
 */
export class HttpAuthGateway implements AuthGateway {
  async signIn(credentials: Credentials): Promise<Session> {
    const result = await request('/auth/login', {
      method: 'POST',
      body: credentials,
      schema: AuthResultSchema,
      // Asked once more by itself when no answer comes: a phone waking the app loses
      // its first request, and signing in twice does no harm. Request of 9 October 2026.
      retryNoAnswer: 1,
    });

    setAccessToken(result.accessToken);
    // Nothing the last person on this device was shown is served to this one.
    await forgetCachedWork();
    // Kept only once the sign in succeeded, and forgotten by a sign in that
    // left the box unticked: the box is the whole of the person's decision.
    rememberUsername(credentials.remember === true ? credentials.username : null);
    return result.session;
  }

  rememberedUsername(): string | null {
    try {
      return localStorage.getItem(REMEMBERED_USERNAME_KEY);
    } catch {
      return null;
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await request('/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    });
  }

  async reportSignInLocation(
    position: { latitude: number; longitude: number } | null,
  ): Promise<void> {
    await request('/auth/sign-in-location', {
      method: 'POST',
      body: { latitude: position?.latitude ?? null, longitude: position?.longitude ?? null },
    });
  }

  async signOut(): Promise<void> {
    try {
      await request('/auth/logout', { method: 'POST' });
    } finally {
      // Whatever the server said, this device must stop holding the token,
      // and the answers cached for this person.
      clearAccessToken();
      await forgetCachedWork();
    }
  }

  async session(): Promise<Session | null> {
    try {
      return await request('/auth/session', { schema: SessionSchema });
    } catch (error) {
      // Not signed in is an answer, not a failure. Anything else is a failure.
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  }

  async refresh(): Promise<Session | null> {
    try {
      const result = await request('/auth/refresh', {
        method: 'POST',
        schema: AuthResultSchema,
      });
      setAccessToken(result.accessToken);
      return result.session;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  }
}
